import { IntegrationLimitedDto } from '@/lib/integrationTypes/integrationTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldValues } from 'react-hook-form';
import { z } from 'zod';

const createUriSchema = (allowedProtocols?: string[]) => {
  return z.string().min(1).refine(
    (val) => {
      try {
        new URL(val);
        if (!allowedProtocols || allowedProtocols.length === 0) {
          return true; // Accept any valid URL
        }
        // Extract protocol from the value
        const match = val.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)/);
        if (!match) return false;
        const proto = match[1].toLowerCase();
        // Check protocol matches allowed list (case-insensitive)
        return allowedProtocols.some((p) => p.toLowerCase() === proto);
      } catch {
        return false;
      }
    },
    () => {
      if (!allowedProtocols || allowedProtocols.length === 0) {
        return { message: 'Must be a valid URI (e.g., https://example.com or rtsp://camera.local)' };
      }
      return { message: `Must use: ${allowedProtocols.join(', ')}` };
    }
  );
};

const dataTypeMap: Record<string, z.Schema> = {
  number: z.coerce.number(),
  boolean: z.boolean(),
  password: z.string().min(1),
  string: z.string().min(1),
  select: z.string().min(1),
  // Note: 'uri' is handled dynamically in buildFormSchema
};

const defaultValuesMap: Record<string, string | boolean | number> = {
  number: 0,
  boolean: false,
  password: '',
  string: '',
  select: '',
  uri: '',
};

export function buildFormSchema(
  integration: IntegrationLimitedDto | undefined,
  initialValues: FieldValues = {}
) {
  if (!integration) {
    return { schema: z.object({}), defaultValues: {} };
  }

  const properties: Record<string, z.Schema> = {};
  const defaultValues: Record<string, any> = {};

  function emptyToUndefined(v: unknown) {
    return v === '' ? undefined : v;
  }

  for (const field of integration.integrationFields) {
    const isArray = /\[\]$/.test(field.dataType);
    const baseType = field.dataType.replace(/\[\]$/, '');
    const options = field.options?.filter((option): option is string => Boolean(option?.length));

    // Handle URI type with dynamic protocol validation
    if (baseType === 'uri') {
      // For URI, options contain allowed protocols (if any)
      const allowedProtocols = options && options.length > 0 ? options : undefined;
      const uriSchema = createUriSchema(allowedProtocols);

      if (isArray) {
        const arrSchema = z.array(uriSchema);
        properties[field.id] = field.optional ? arrSchema : arrSchema.min(1);
        const provided = initialValues[field.id];
        defaultValues[field.id] = Array.isArray(provided)
          ? provided
          : (field.optional ? [] : ['']);
      } else {
        properties[field.id] = field.optional
          ? z.preprocess(emptyToUndefined, uriSchema.optional())
          : uriSchema;
        defaultValues[field.id] = initialValues[field.id] ?? (field.optional ? undefined : '');
      }
      continue;
    }

    const fallbackSchema = dataTypeMap[baseType] ?? z.string().min(1);
    const fallbackDefault = defaultValuesMap[baseType] ?? '';

    // Handle select type with enum options (options are dropdown choices)
    if (options && options.length > 0 && baseType === 'select') {
      const uniqueOptions = Array.from(new Set(options));
      if (uniqueOptions.length > 0) {
        const [firstOption, ...restOptions] = uniqueOptions;
        const enumSchema = z.enum([firstOption, ...restOptions] as [string, ...string[]]);

        if (isArray) {
          const arrSchema = z.array(enumSchema);
          properties[field.id] = field.optional ? arrSchema : arrSchema.min(1);
          const provided = initialValues[field.id];
          defaultValues[field.id] = Array.isArray(provided)
            ? provided
            : (field.optional ? [] : [firstOption]);
        } else {
          const scalar = field.optional
            ? z.preprocess(emptyToUndefined, enumSchema.optional())
            : enumSchema;
          properties[field.id] = scalar;
          defaultValues[field.id] = initialValues[field.id] ?? (field.optional ? undefined : firstOption);
        }
        continue;
      }
    }

    if (isArray) {
      const arrSchema = z.array(fallbackSchema);
      properties[field.id] = field.optional ? arrSchema : arrSchema.min(1);
      const provided = initialValues[field.id];
      defaultValues[field.id] = Array.isArray(provided)
        ? provided
        : (field.optional ? [] : [fallbackDefault]);
    } else {
      properties[field.id] = field.optional
        ? z.preprocess(emptyToUndefined, fallbackSchema.optional())
        : fallbackSchema;
      defaultValues[field.id] = initialValues[field.id] ?? (field.optional ? undefined : fallbackDefault);
    }
  }

  const schema = z.object(properties);

  return { schema, resolver: zodResolver(schema), defaultValues };
}
